import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  Icons,
  Stack,
  TextField,
  Typography,
  ToggleButtonGroup,
  Link,
  RadioGroup,
  FormControlLabel,
} from '@nextiva/next-ui-components';
import { Controller, useForm, SubmitHandler } from 'react-hook-form-new';
import { isValidEmail } from 'utils/validators';
import {
  IRequestWebsiteForm,
  RequestWebsiteFormProps,
} from 'interface/WizardV3';
import { useWizardContextV3 } from '../../CampaignDialog/WizardContextV3/WizardContextV3';
import { OverlineTypo } from '../../CampaignDialog/WizardV3.styled';
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from '../../CampaignDialog/WizardContextV3/customValidations';
import {
  cardContent,
  REQUEST_WEBSITE_FORM,
  SUBSCRIPTION_TYPE,
} from '../../CampaignDialog/constants';
import { CustomToggleButton, LabelDiscount } from './RequestWebsiteForm.styled';
import { PriceCard } from './PriceCard';

const dataTestId = 'PlatformAdminUI-Messaging-RequestWebsiteForm';
export function RequestWebsiteForm({
  open,
  handleClose,
}: RequestWebsiteFormProps) {
  const { enablementInfo, handleSendEmail } = useWizardContextV3();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<IRequestWebsiteForm>({
    mode: 'all',
    defaultValues: {
      [REQUEST_WEBSITE_FORM.USER_EMAIL]:
        enablementInfo?.businessEmail || enablementInfo?.email || '',
      [REQUEST_WEBSITE_FORM.PHONE_NUMBER]: '',
      [REQUEST_WEBSITE_FORM.SUBSCRIPTION_TYPE]: SUBSCRIPTION_TYPE.MONTHLY,
      [REQUEST_WEBSITE_FORM.PACKAGE_SELECTED]: 'Pro',
    },
  });
  const isReadyToSubmit = isValid;
  const onSubmit: SubmitHandler<IRequestWebsiteForm> = (data) => {
    handleSendEmail(data);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      data-testid={`${dataTestId}-Dialog`}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '960px',
          width: '960px',
        },
      }}
    >
      <DialogTitle
        variant="h5"
        sx={{ padding: '28px 32px' }}
        data-testid={`${dataTestId}-Title`}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5" color="#0B1029">
            Request website
          </Typography>
          <IconButton
            aria-label="close-request-website-dialog"
            onClick={handleClose}
            data-testid={`${dataTestId}-Title-CloseButton`}
          >
            <Icon icon={Icons.close} size={24} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ padding: '0px 32px' }}>
        <Divider />
      </DialogContent>
      <DialogContent sx={{ padding: '32px' }}>
        <DialogContentText sx={{ paddingBottom: '16px' }}>
          <Typography variant="body2" fontWeight={800} color="#0B1029">
            Provide contact information
          </Typography>
          <Typography variant="caption" color="#55596B">
            Our team will reach out to you for the website requirements.
          </Typography>
        </DialogContentText>
        <Grid container direction="row" spacing={2} columns={12}>
          <Grid item xs={4}>
            <Controller
              control={control}
              name={REQUEST_WEBSITE_FORM.USER_EMAIL}
              rules={{
                required: true,
                validate: isValidEmail,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="email-textfield"
                  data-testid={`${dataTestId}-Email-TextField`}
                  label={<OverlineTypo>Email *</OverlineTypo>}
                  fullWidth
                  value={value || ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  size="small"
                  error={!!errors.userEmail}
                  helperText={!!errors.userEmail && 'Please enter valid email'}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              control={control}
              name={REQUEST_WEBSITE_FORM.PHONE_NUMBER}
              rules={{
                validate: (value) => {
                  if (!value) {
                    return true;
                  }
                  return validatePhoneNumber(value);
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="phone-number-requestWebsite-textfield"
                  data-testid={`${dataTestId}-PhoneNumber-TextField`}
                  label={<OverlineTypo>Phone Number</OverlineTypo>}
                  fullWidth
                  value={value || ''}
                  onChange={(e) => {
                    onChange(formatPhoneNumber(e.target.value));
                  }}
                  onBlur={onBlur}
                  placeholder="Enter phone number"
                  size="small"
                  error={!!errors.phoneNumber}
                  helperText={
                    !!errors.phoneNumber && 'Please enter valid phone number'
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        <Divider sx={{ margin: '32px 0px' }} />
        <DialogContentText>
          <Grid
            container
            direction="row"
            columns={12}
            justifyContent=""
            alignItems="start"
          >
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={800} color="#0B1029">
                Select package
              </Typography>
              <Typography variant="caption" color="#55596B">
                Get your website with a custom domain.
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Controller
                name={REQUEST_WEBSITE_FORM.SUBSCRIPTION_TYPE}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <ToggleButtonGroup
                    data-testid={`${dataTestId}-ToggleButtonGroup`}
                    exclusive
                    onChange={(_e, value) => {
                      if (value !== null) field.onChange(value);
                    }}
                    size="small"
                    color="standard"
                    sx={{ border: '1px solid #DEDFE4' }}
                    value={field.value}
                  >
                    <CustomToggleButton
                      data-testid={`${dataTestId}-Monthly`}
                      key={SUBSCRIPTION_TYPE.MONTHLY}
                      value={SUBSCRIPTION_TYPE.MONTHLY}
                    >
                      <Typography variant="caption">Monthly</Typography>
                    </CustomToggleButton>
                    <CustomToggleButton
                      data-testid={`${dataTestId}-Yearly`}
                      key={SUBSCRIPTION_TYPE.YEARLY}
                      value={SUBSCRIPTION_TYPE.YEARLY}
                    >
                      <Typography variant="caption">Yearly</Typography>
                      <LabelDiscount
                        checked={field.value === SUBSCRIPTION_TYPE.YEARLY}
                        type="primary"
                      >
                        -33%
                      </LabelDiscount>
                    </CustomToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={0} marginTop={2}>
            <Controller
              control={control}
              name={REQUEST_WEBSITE_FORM.PACKAGE_SELECTED}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => {
                const watchSubscriptionType = watch('subscriptionType');
                return (
                  <>
                    <RadioGroup
                      data-testid={`${dataTestId}-RadioGroup`}
                      name="radio-buttons-website-package-selected"
                      value={value || ''}
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.value)}
                    >
                      <Grid
                        container
                        rowSpacing={0}
                        columnSpacing={2}
                        marginTop={2}
                      >
                        {cardContent.map((card) => (
                          <Grid item key={card.title} xs={4}>
                            <FormControlLabel
                              value={card.value}
                              label=""
                              control={
                                <PriceCard
                                  key={card.title}
                                  card={card}
                                  isSelected={value === card.value}
                                  isMonthly={
                                    watchSubscriptionType ===
                                    SUBSCRIPTION_TYPE.MONTHLY
                                  }
                                />
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                    {!!errors.packageSelected && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ marginTop: '4px' }}
                      >
                        Please select a website package.
                      </Typography>
                    )}
                  </>
                );
              }}
            />
          </Grid>
          <Grid container spacing={0} marginTop={2}>
            <Typography variant="caption" color="#616576">
              <Link
                href="https://www.nextiva.cx/pricing"
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${dataTestId}-PricingLink`}
                fontWeight={400}
              >
                Learn more
              </Link>{' '}
              about our website services.
            </Typography>
          </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogContent sx={{ padding: '0px 32px' }}>
        <Divider />
      </DialogContent>
      <DialogActions sx={{ padding: '24px 32px' }}>
        <Button
          data-testid={`${dataTestId}-CancelButton`}
          color="secondary"
          onClick={handleClose}
          variant="contained"
          sx={{
            '&.MuiButton-sizeMedium': {
              padding: '12px 18px',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid={`${dataTestId}-SendButton`}
          disabled={!isReadyToSubmit}
          color="primary"
          variant="contained"
          sx={{
            '&.MuiButton-sizeMedium': {
              padding: '12px 16px',
              marginLeft: '16px',
            },
          }}
          onClick={handleSubmit(onSubmit)}
        >
          Send request
        </Button>
      </DialogActions>
    </Dialog>
  );
}
